import React from "react";

const PAGE_SIZES = [10, 20, 30, 50, 100];

/**
 * Reusable pagination control. Designed to be parented by admin list pages.
 *
 * Props:
 *  - page (1-indexed), limit, total : numbers
 *  - onPageChange(p), onLimitChange(l) : callbacks
 *  - testid : prefix for data-testid attributes
 */
export default function Pagination({ page, limit, total, onPageChange, onLimitChange, testid = "pagination" }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (limit || 1)));
  const safePage = Math.min(Math.max(1, page || 1), totalPages);

  // Build a compact list of page buttons: 1 … (page-1) page (page+1) … totalPages
  const buttons = [];
  const push = (p) => buttons.push(p);
  const ELLIPSIS = "…";
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i += 1) push(i);
  } else {
    push(1);
    if (safePage > 3) push(ELLIPSIS);
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i += 1) push(i);
    if (safePage < totalPages - 2) push(ELLIPSIS);
    push(totalPages);
  }

  const from = total === 0 ? 0 : (safePage - 1) * limit + 1;
  const to = Math.min(total, safePage * limit);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mt-3 px-4 py-3 border-t border-[#E1E8F0] bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-[#5C6B7A]">
        <span>Rows per page:</span>
        <select
          value={limit}
          onChange={(e) => onLimitChange(Number(e.target.value))}
          className="px-2 py-1 rounded-md border border-[#E1E8F0] bg-white text-xs text-[#0F2540]"
          data-testid={`${testid}-limit`}
        >
          {PAGE_SIZES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <span className="ml-2">{from}–{to} of {total}</span>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(safePage - 1)}
          disabled={safePage <= 1}
          className="text-xs px-3 py-1.5 rounded-full border border-[#E1E8F0] bg-white text-[#0F2540] disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#1E4F87]"
          data-testid={`${testid}-prev`}
        >
          ‹ Prev
        </button>
        {buttons.map((p, idx) => p === ELLIPSIS ? (
          <span key={`e-${idx}`} className="px-2 text-xs text-[#5C6B7A]">{ELLIPSIS}</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            className={`text-xs min-w-[28px] px-2 py-1.5 rounded-full border ${p === safePage ? "bg-[#0F2540] border-[#0F2540] text-white" : "bg-white border-[#E1E8F0] text-[#0F2540] hover:border-[#1E4F87]"}`}
            data-testid={`${testid}-page-${p}`}
          >
            {p}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage >= totalPages}
          className="text-xs px-3 py-1.5 rounded-full border border-[#E1E8F0] bg-white text-[#0F2540] disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#1E4F87]"
          data-testid={`${testid}-next`}
        >
          Next ›
        </button>
      </div>
    </div>
  );
}
